import { useState, useEffect, useContext } from "react";
import placeholder from "../inc/placeholder.jpg";
import { SiteContext } from "../contexts/SiteContext";
import { ViewContext } from "../contexts/ViewContext";
import {
	Paper,
	Hidden,
	Typography,
	IconButton,
	Card,
	CardActionArea,
	CardContent,
	CardMedia,
	makeStyles,
	Fade,
	Tooltip,
	Grid,
	Button
} from "@material-ui/core";
import { Link } from "react-router-dom";
import ViewListIcon from "@material-ui/icons/ViewList";
import ViewGridIcon from "@material-ui/icons/ViewModule";
import { parse } from 'node-html-parser';

const useStyles = makeStyles((theme) => ({
	paper: {
		padding: "1rem",
		marginBottom: "1rem",
		position: 'relative'
	},
	media: { paddingBottom: "100%" },
	horizCard: {
		marginBottom: "1rem",
	},
	link: {
		color: "black",
		textDecoration: "none",
	},
	horizCardActionArea: {
		display: "flex",
		justifyContent: "start",
	},
	horizMedia: {
		paddingBottom: "20%",
		paddingRight: "20%",
	},
	horizCardContent: {},
	personBio: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		display: 'box',
		lineClamp: '6',
		boxOrient: 'vertical'
	}
}));

//---------------
// MAIN COMPONENT
//---------------

function People({ unit, theme }) {
	const classes = useStyles();
	const [people, setPeople] = useState();
	const [loading, setLoading] = useState(true);
	const { peopleView, setPeopleView } = useContext(ViewContext);

	useEffect(() => {
		const fetchData = async () => {
			let url;
			if (unit) {
				if (unit.people) {
					// Filter out unpublished people
					let unitPeople = unit.people.filter((x) => x.published)
					unitPeople = unitPeople.map(person => {
						let parsedBio = parse(person.bio);
						let bioTextOnly = parsedBio.rawText.slice(0, 1000) + '...';
						return { ...person, "bio": bioTextOnly }
					});
					setPeople(unitPeople);
				}
			} else if (theme) {
				url = `${process.env.REACT_APP_API_URL}/people?sort=lastName&themes[in]=${theme._id}`;
				const res = await fetch(url, { credentials: "include" });
				let allPeople = await res.json();
				if (allPeople.success) {
					// Filter out unpublished people
					allPeople.data = allPeople.data.filter((x) => x.published)
					allPeople.data = allPeople.data.map(person => {
						let parsedBio = parse(person.bio);
						let bioTextOnly = parsedBio.rawText.slice(0, 1000) + '...';
						return { ...person, "bio": bioTextOnly }
					});
					setPeople(allPeople.data);
				} else {
					console.log(allPeople.error);
				}
			}
		};
		fetchData();
		setLoading(false);
	}, [unit, theme]);

	if (loading || !peopleView) {
		return "Loading...";
	}

	return (
		<>

			{people && people.length > 0 && (
				<Fade in={!loading}>
					<Paper className={classes.paper}>


						<Typography
							component="h3"
							variant="h6"
							align="center"
							style={{ marginBottom: '1rem' }}
						>
							People
						</Typography>
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								position: 'absolute',
								top: '.5rem',
								right: '.5rem'
							}}
						>
							<Hidden smDown>
								{peopleView === "grid" ? (
									<Tooltip
										title="View people as a list"
										aria-label="View people as a list"
										placement="top"
									>
										<IconButton
											aria-label="View the people in a list format"
											onClick={() => setPeopleView("list")}
											variant="contained"
										>
											<ViewListIcon />
										</IconButton>
									</Tooltip>
								) : (
									<Tooltip
										title="View people as a grid"
										aria-label="View people as a grid"
										placement="top"
									>
										<IconButton
											aria-label="View the people in a grid format"
											onClick={() => setPeopleView("grid")}
											variant="contained"
										>
											<ViewGridIcon />
										</IconButton>
									</Tooltip>
								)}
							</Hidden>
						</div>

						<Hidden smDown>
							{peopleView === "list" ? (
								<PeopleList people={people} />
							) : (
								<PeopleGrid people={people} />
							)}
						</Hidden>

						{/*On mobile devices, only allow displaying the EntriesGrid*/}
						<Hidden mdUp>
							<PeopleGrid people={people} />
						</Hidden>
					</Paper>
				</Fade >
			)
			}
		</>
	);
}

const fullName = (person) => {
	let name = "";
	if (person.firstName) {
		name += person.firstName;
	}
	if (person.middleName) {
		name += " " + person.middleName;
	}
	if (person.lastName) {
		name += " " + person.lastName;
	}
	if (person.suffix) {
		name += " " + person.suffix;
	}
	if (person.title) {
		name += ", " + person.title;
	}
	return name;
};

//----------
// GRID VIEW
//----------

function PeopleGrid({ people }) {
	const classes = useStyles();
	const { selectedSite } = useContext(SiteContext);

	if (!people) {
		return "Please pass people to the PeopleGrid component...";
	}

	return (
		<Grid container spacing={3}>
			{people.map((person, index) => {
				return (
					<Grid item key={index} xs={12} sm={4} md={3} lg={2}>
						<Card elevation={0} className={classes.card} aria-label>
							<Link
								className={classes.link}
								to={`/${selectedSite.slug}/people/${person.slug}`}
							>
								<CardActionArea>
									<CardMedia
										className={classes.media}
										image={
											person.featuredImage
												? `${process.env.REACT_APP_FILE_URL}/${person.featuredImage.thumbnailName}`
												: placeholder
										}
									/>
									<CardContent>
										<Typography align="center" component="h4">
											{fullName(person)}
										</Typography>
									</CardContent>
								</CardActionArea>
							</Link>
						</Card>
					</Grid>
				);
			})}
		</Grid>
	);
}

//----------
// LIST VIEW
//----------
//
function PeopleList({ people }) {
	const classes = useStyles();
	const { selectedSite } = useContext(SiteContext);

	if (!people) {
		return "Please pass people to the PeopleList component...";
	}

	return (
		<>
			{people.map((person, index) => {
				return (
					<Card key={index} className={classes.horizCard}>
						<Link
							className={classes.link}
							to={`/${selectedSite.slug}/people/${person.slug}`}
						>
							<CardActionArea className={classes.horizCardActionArea}>
								<CardMedia
									className={classes.horizMedia}
									image={
										person.featuredImage
											? `${process.env.REACT_APP_FILE_URL}/${person.featuredImage.thumbnailName}`
											: placeholder
									}
									title={person.title}
								/>
								<CardContent className={classes.horizCardContent}>
									<Typography variant="h6" component="h4">
										{fullName(person)}
									</Typography>
									<Typography
										component="p"
										className={classes.personBio}
										dangerouslySetInnerHTML={{ __html: person.bio }}
									></Typography>
									<Button variant="contained" style={{ marginTop: '1rem' }}>Read More</Button>
								</CardContent>
							</CardActionArea>
						</Link>
					</Card>
				);
			})}
		</>
	);
}

export default People;
